import type { AnyRouteMatch } from '@tanstack/react-router';
import { siteMetadata } from '../types/meta';

type Meta = NonNullable<AnyRouteMatch['meta']>;

interface SeoOptions {
  /** Page title, the site title is appended to it (e.g. "Kosár - Pixwrist"). The site title alone if not set. */
  title?: string;
  /** Inherited from the site defaults (root route) if not set. */
  description?: string | null;
  /** Absolute URL or a site-relative path. Inherited from the site defaults (root route) if not set. */
  image?: string | null;
}

export function toAbsoluteUrl(pathOrUrl: string) {
  return /^https?:\/\//.test(pathOrUrl) ? pathOrUrl : (
      `${siteMetadata.siteUrl}${pathOrUrl}`
    );
}

/**
 * Meta tags of a page for a route's `head()`, the counterpart of the SEO component in the old (Gatsby) project.
 *
 * Only the given values are emitted: TanStack Router renders the tags of all matched routes and, for the title and
 * for meta tags with the same `name` / `property`, keeps the deepest route's one. So a page only needs to set what
 * differs from the site defaults set in the root route (see routes/__root.tsx).
 *
 * @example
 * head: () => ({ meta: seo({ title: 'Kosár' }) })
 */
export function seo({ title, description, image }: SeoOptions = {}): Meta {
  const fullTitle =
    title ? `${title} - ${siteMetadata.title}` : siteMetadata.title;
  const imageUrl = image ? toAbsoluteUrl(image) : null;

  return [
    { title: fullTitle },
    { property: 'og:title', content: fullTitle },
    ...(description ?
      [
        { name: 'description', content: description },
        { property: 'og:description', content: description }
      ]
    : []),
    ...(imageUrl ?
      [
        { name: 'image', content: imageUrl },
        { property: 'og:image', content: imageUrl }
      ]
    : [])
  ];
}

/** Meta tags of the 404 page (the prerendered /404 page and any page not found at runtime). */
export function notFoundSeo(): Meta {
  return [
    ...seo({ title: '404 - Az oldal nem található' }),
    { name: 'robots', content: 'noindex' }
  ];
}
